/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================
*/

import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import i18n from "i18next";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";

import FormSelector from "components/FormSelector";
import FormDateTimePicker from "components/FormDateTimePicker";

import FormField from "components/FormField";
import { buildClientReservationSelectorOptions } from "layouts/clients/clientReservationsUtils";
import serviceUtils from "../project/components/servicesConfig/serviceUtils";

const validate = (values) => {
  const errors = {};
  const requiredFields = ["clientReservationId", "startDate", "endDate", "serviceRequestNumber"];

  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = i18n.t("Required");
    }
  });

  if (values.serviceRequestNumber !== undefined) {
    const num = Number(values.serviceRequestNumber);

    if (Number.isNaN(num)) {
      errors.serviceRequestNumber = i18n.t("mustBeANumber");
    } else if (num < 1) {
      errors.serviceRequestNumber = i18n.t("mustBeMinOne");
    }
  }

  return errors;
};

function ServiceEventForm(props) {
  const {
    handleSubmit,
    event,
    pristine,
    onSubmit,
    onRemove,
    actionType,
    apartments,
    projectId,
    projects,
    clients,
    clientReservations,
    selectedClientReservationId,
    startDate,
    change,
    service,
  } = props;

  const { t } = useTranslation();

  const projectTimeZone = projects[projectId]?.timeZone || "UTC";

  const { statusValues, formatTimestampToProjectTimeZone, getClientReservationsDerived } =
    serviceUtils;

  const { clientReservationsById, activeClientReservations } = useMemo(
    () => getClientReservationsDerived(clientReservations),
    [clientReservations]
  );

  const activeReservationOptions = useMemo(
    () =>
      buildClientReservationSelectorOptions({
        reservations: activeClientReservations,
        clients,
        apartments,
        formatTimestamp: formatTimestampToProjectTimeZone,
        getTimeZone: () => projectTimeZone,
      }),
    [
      activeClientReservations,
      clients,
      apartments,
      formatTimestampToProjectTimeZone,
      projectTimeZone,
    ]
  );

  const selectedClientReservation =
    clientReservationsById[selectedClientReservationId] ||
    clientReservationsById[event?.extendedProps?.clientReservationId] ||
    null;

  const selectedApartmentId =
    selectedClientReservation?.apartmentId || event?.extendedProps?.apartmentId || "";

  const isAssociatedEvent = Boolean(event?.extendedProps?.isAssociated || event?.isAssociated);
  const isEditAssociatedEvent = actionType === "edit" && isAssociatedEvent;

  useEffect(() => {
    const { serviceDuration } = service;
    const endDate = new Date(startDate.valueOf() + serviceDuration * 60 * 1000);
    change("endDate", endDate);
  }, [startDate]);

  const serviceName = service?.name || t("service");

  const submitData = (data) => {
    // console.log("ServiceEventForm", { data, actionType });

    const { endDate, serviceId, clientReservationId, status, serviceRequestNumber } = data;

    const originalStartDate = new Date(event?.start || event?.startDate);
    const originalEndDate = new Date(event?.end || event?.endDate);

    const startDateToSend =
      isEditAssociatedEvent && !Number.isNaN(originalStartDate.valueOf())
        ? originalStartDate.toISOString()
        : startDate.toISOString();

    const endDateToSend =
      isEditAssociatedEvent && !Number.isNaN(originalEndDate.valueOf())
        ? originalEndDate.toISOString()
        : endDate.toISOString();

    const serviceRequestNumberToSend = serviceRequestNumber;

    const selectedReservation = clientReservationsById[clientReservationId] || null;
    const finalApartmentId = selectedReservation?.apartmentId || selectedApartmentId;
    const clientUserId =
      selectedReservation?.clientId ||
      event?.extendedProps?.clientUserId ||
      event?.clientUserId ||
      "";

    if (!clientReservationId || !finalApartmentId || !clientUserId) return;

    const name = apartments[finalApartmentId]?.name || "";

    const eventToSend = {
      apartmentId: finalApartmentId,
      end: endDateToSend,
      start: startDateToSend,
      clientUserId,
      clientReservationId,
      title: name,
      id: actionType === "edit" ? event?.id : undefined,
      isAssociated: event?.extendedProps?.isAssociated || event?.isAssociated || false,
      serviceRequestNumber: serviceRequestNumberToSend,
      serviceId,
      status,
    };

    onSubmit(eventToSend, actionType);
  };

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(submitData)}>
        <Grid container justifyContent="center" alignItems="center" sx={{ overflow: "visible" }}>
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("service")} {serviceName}
            </MDBox>
            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                {/* CLIENT RESERVATION SELECTOR */}
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Field
                    name="clientReservationId"
                    values={activeReservationOptions.values}
                    names={activeReservationOptions.names}
                    component={FormSelector}
                    label={t("client")}
                    disabled={actionType === "edit"}
                  />
                </Grid>

                {/* SERVICE NUMBER */}
                <Grid size={{ xs: 4 }}>
                  <Field
                    sx={{ maxWidth: "200px" }}
                    name="serviceRequestNumber"
                    component={FormField}
                    label={t("serviceNumber")}
                    type="number"
                    min={1}
                    parse={(value) => Number(value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 12 }}>
                  <Field
                    name="status"
                    values={statusValues.map((s) => s?.status)}
                    names={statusValues.map((s) => ({ uid: s?.status, name: t(s?.label) }))}
                    component={FormSelector}
                    label={t("state")}
                  />
                </Grid>

                {/* STARTDATE / ENDDATE */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="startDate"
                    component={FormDateTimePicker}
                    label={t("startDate")}
                    disabled={isEditAssociatedEvent}
                    timeZone={projectTimeZone}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="endDate"
                    disabled
                    component={FormDateTimePicker}
                    label={t("endDate")}
                    timeZone={projectTimeZone}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} display="flex" flexDirection="row" justifyContent="center">
                  <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                    {/* SAVE */}
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={pristine}
                    >
                      {t("Save")}
                    </MDButton>
                  </MDBox>
                  {/* DELETE */}
                  {actionType === "edit" && (
                    <MDBox display="flex" justifyContent="center" mt={2} ml={2}>
                      <MDButton
                        variant="gradient"
                        color="error"
                        size="small"
                        onClick={() => onRemove(event)}
                      >
                        {t("Delete")}
                      </MDButton>
                    </MDBox>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ServiceEventForm.defaultProps = {
  projects: {},
  apartments: {},
  projectId: "",
  selectedClientReservationId: "",
  event: {},
  startDate: {},
  service: {},
  clients: {},
  clientReservations: {},
};

ServiceEventForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  projects: PropTypes.object,
  event: PropTypes.object,
  startDate: PropTypes.object,
  apartments: PropTypes.object,
  selectedClientReservationId: PropTypes.string,
  clients: PropTypes.object,
  clientReservations: PropTypes.object,
  service: PropTypes.object,
};

const selector = formValueSelector("ServiceEventForm");

const mapStateToProps = (state, props) => {
  const { actionType, event, newEvent, service, projectId } = props;
  const {
    apartmentsReducer,
    projectsReducer,
    clientsReducer,
    clientReservationsReducer,
    mainUserReducer,
  } = state;

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};

  const { serviceDuration, uid } = service;
  const serviceId = uid;

  let startDate = Date.now();
  let endDate = new Date(startDate.valueOf() + serviceDuration * 60 * 1000);
  let selectedClientReservationId = "";

  // Usamos SIEMPRE la misma función de parseo que el calendario
  if (actionType === "add") {
    const { start } = newEvent;
    startDate = start;
    endDate = new Date(start.valueOf() + serviceDuration * 60 * 1000);
  } else {
    const { start, end, extendedProps } = event;

    selectedClientReservationId = extendedProps?.clientReservationId || event?.clientReservationId;
    startDate = new Date(start);
    endDate = new Date(end);
  }

  const existingNumber =
    event?.extendedProps?.serviceRequestNumber ?? event?.serviceRequestNumber ?? 1;

  const allClientReservations = clientReservationsReducer.clientReservations || {};

  const clientReservations = Object.entries(allClientReservations || {}).reduce(
    (accReservationsByClient, [clientId, reservationsByClient]) => {
      const filteredReservationsByClient = Object.values(reservationsByClient || {}).reduce(
        (accReservations, reservation) => {
          if (!reservation?.uid) return accReservations;

          const reservationProjectId = reservation?.projectId;

          if (projectId && reservationProjectId !== projectId) return accReservations;

          if (isProjectManager && !userProjects?.[reservationProjectId]) return accReservations;

          return {
            ...accReservations,
            [reservation.uid]: reservation,
          };
        },
        {}
      );

      if (Object.keys(filteredReservationsByClient).length === 0) return accReservationsByClient;

      return {
        ...accReservationsByClient,
        [clientId]: filteredReservationsByClient,
      };
    },
    {}
  );

  return {
    initialValues: {
      startDate,
      endDate,
      clientReservationId: selectedClientReservationId,
      serviceId,
      serviceRequestNumber: actionType === "add" ? 1 : existingNumber,
      status: actionType === "add" ? "pending" : event?.extendedProps?.status || "pending",
    },
    startDate: selector(state, "startDate"),
    selectedClientReservationId: selector(state, "clientReservationId"),
    apartments: apartmentsReducer,
    projects: projectsReducer,
    clients: clientsReducer,
    clientReservations,
    service,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "ServiceEventForm",
    validate,
    enableReinitialize: true,
  })(ServiceEventForm)
);
